import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CopyContentBuilderRequest, PresentationId, PresentationResponse } from '@business/entities/presentation';
import { PresentationAPI } from '@business/api/presentation';
import { PresentationSettingsFormData } from '@modules/presentation/PresentationPage/PresentationSettingsModal/PresentationSettingsModal';
import { PresentationBuilderFormData } from '@modules/presentation/PresentationPage/PresentationBuilder/PresentationBuilderPage';
import { PresentationEntryFormData } from '@modules/form/AddPresentationModal/AddPresentationModal';
import { OrgId } from '@business/entities/organization';
import { StartPresentationFormData } from '@modules/presentation/StartPresentationModal/StartPresentationModal';

export class PresentationBloc extends BaseBloc {
  private readonly presentationRepo = new Repository<PresentationResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.presentationRepo.items$;
  readonly selectItems$ = this.presentationRepo.selectItem;

  onReset() {
    this.presentationRepo.reset();
  }

  fetchPresentations = () => {
    return PresentationAPI.fetchPresentations().pipe(
      this.presentationRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_presentations_error') }),
    );
  };

  fetchPresentation = (id: PresentationId) => {
    return PresentationAPI.fetchPresentation(id).pipe(
      this.presentationRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_presentations_error') }),
    );
  };

  createPresentation = (item: Partial<PresentationResponse>) => {
    return PresentationAPI.createPresentation(item).pipe(
      this.presentationRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_presentation'),
      }),
    );
  };

  updatePresentation = (id: PresentationId, item: Partial<PresentationResponse>, autoSave = false) => {
    if (autoSave) {
      return PresentationAPI.updatePresentation(id, item).pipe(this.presentationRepo.ops.upsertOne(item => ({ item })));
    }
    return PresentationAPI.updatePresentation(id, item).pipe(
      this.presentationRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_presentation'),
      }),
    );
  };

  duplicatePresentation = (id: PresentationId, item: Partial<PresentationResponse>) => {
    return PresentationAPI.duplicatePresentation(id, item).pipe(
      this.presentationRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('duplicate_presentation'),
      }),
    );
  };

  deletePresentation = (presentationId: PresentationId) => {
    return showDeleteConfirmation('Delete Presentation', 'Do you really want to remove this presentation?').pipe(
      switchMap(() => PresentationAPI.deletePresentation(presentationId)),
      this.presentationRepo.ops.removeOne(() => presentationId),
      handleMessage({
        type: requestMessage('delete_presentation'),
      }),
    );
  };

  updateSettings = (id: PresentationId, item: Partial<PresentationSettingsFormData>) => {
    return PresentationAPI.updateSettings(id, item).pipe(
      this.presentationRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_presentation'),
      }),
    );
  };

  fetchPublicPresentation = (id: PresentationId, isTemplatePreviewPage = true, orgId?: OrgId, isPublic = false) => {
    return PresentationAPI.fetchPublicPresentation(id, isTemplatePreviewPage, orgId, isPublic);
  };

  fetchPublicDetailPresentation = (id: PresentationId, isTemplatePreviewPage = true, orgId: OrgId) => {
    return PresentationAPI.fetchPublicDetailPresentation(id, isTemplatePreviewPage, orgId);
  };

  onNextStep = (
    id: PresentationId,
    stepId: string,
    data: Record<string, string>,
    isTemplatePreviewPage = true,
    orgId?: OrgId,
    isPublic = false,
    formRawData?: any,
  ) => {
    return PresentationAPI.onNextStep(id, stepId, data, isTemplatePreviewPage, orgId, isPublic, formRawData);
  };

  onBackStep = (id: PresentationId, stepId: string, isTemplatePreviewPage = true, orgId?: OrgId, isPublic = false) => {
    return PresentationAPI.onBackStep(id, stepId, isTemplatePreviewPage, orgId, isPublic);
  };

  fetchPresentationStep = (id: PresentationId, stepId: string) => {
    return PresentationAPI.fetchPresentationStep(id, stepId).pipe(
      handleMessage({
        error: requestMessage('fetch_presentations_error'),
      }),
    );
  };

  updateBuilderFormData = (presentationId: PresentationId, stepId: string, data: string) => {
    return PresentationAPI.updateBuilderFormData(presentationId, stepId, data).pipe(
      handleMessage({
        error: requestMessage('fetch_presentations_error'),
      }),
    );
  };

  addRecommendedLineItem = (presentationId: PresentationId, lineItemId: string, type: string, orgId?: string) => {
    return PresentationAPI.addRecommendedLineItem(presentationId, lineItemId, type, orgId).pipe(
      handleMessage({
        error: requestMessage('fetch_presentations_error'),
      }),
    );
  };

  createPresentationEntry = (orgId: string, folderId: string, data: PresentationEntryFormData) => {
    return PresentationAPI.createPresentationEntry(orgId, folderId, data).pipe(
      handleMessage({
        error: requestMessage('fetch_presentations_error'),
      }),
    );
  };

  updateContentPage = (presentationId: PresentationId, data: CopyContentBuilderRequest, duplicate = false) => {
    if (duplicate) {
      return PresentationAPI.updateContentPage(presentationId, data).pipe();
    } else {
      return PresentationAPI.updateContentPage(presentationId, data).pipe(
        handleMessage({
          error: requestMessage('fetch_presentations_error'),
        }),
      );
    }
  };

  startPresentation = (data: StartPresentationFormData, isPublic = false) => {
    return PresentationAPI.startPresentation(data, isPublic).pipe();
  };
}
